import React from 'react';
import { Layers, LayoutDashboard, Users, BarChart2, Globe, ShieldAlert, Search, Settings, ChevronDown, ChevronRight, ClipboardList, FileClock, Mail, Wrench, History, FileText, User, Truck, BookOpen } from 'lucide-react';
import { UserRole } from '../lib/db';
import { APP_VERSION, APP_BUILD_DATETIME } from '../version';

type NonFloralTab = 'active' | 'history' | 'lara' | 'maintenance' | 'audit';

interface SidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  userRole: UserRole;
  nonFloralTab: NonFloralTab;
  onNonFloralNav: (tab: NonFloralTab) => void;
}

const NON_FLORAL_SUBMENU: { id: NonFloralTab; label: string; icon: any }[] = [
  { id: 'active', label: 'Data Entry / Active LTL', icon: ClipboardList },
  { id: 'history', label: 'Billing History', icon: FileClock },
  { id: 'lara', label: 'Lara Email Queue', icon: Mail },
  { id: 'maintenance', label: 'Account Maintenance', icon: Wrench },
  { id: 'audit', label: 'Audit Log', icon: History },
];

export default function Sidebar({ activeTab, setActiveTab, userRole, nonFloralTab, onNonFloralNav }: SidebarProps) {
  const [nonFloralOpen, setNonFloralOpen] = React.useState(activeTab === 'non-floral');
  
  const allItems = [
    { id: 'dashboard', label: 'CRM Dashboard', icon: LayoutDashboard },
    { id: 'contacts', label: 'CRM Contacts', icon: Users },
    { id: 'cdata-search', label: 'Consignee & Shipper Directory', icon: Search },

    { id: 'analytics', label: 'Analytics', icon: BarChart2 },
    { id: 'google-hub', label: 'Google Workspace', icon: Globe },
    { id: 'client-forms', label: 'Client Forms', icon: FileText },
    { id: 'help-center', label: 'Help & Video Guides', icon: BookOpen },
    { id: 'settings', label: 'System Maintenance', icon: Settings },
    { id: 'admin-settings', label: 'Admin Settings', icon: ShieldAlert },
  ];

  // admin: everything
  // editor (CRM Editor): everything EXCEPT Admin Settings (includes System Maintenance)
  // sales: everything except System Maintenance & Admin Settings
  // warehouse: directory & analytics
  const ROLE_NAV: Record<string, string[]> = {
    admin: ['dashboard', 'contacts', 'cdata-search', 'analytics', 'google-hub', 'client-forms', 'help-center', 'settings', 'admin-settings'],
    editor: ['dashboard', 'contacts', 'cdata-search', 'analytics', 'google-hub', 'client-forms', 'help-center', 'settings'],
    sales: ['dashboard', 'contacts', 'cdata-search', 'analytics', 'google-hub', 'client-forms', 'help-center'],
    warehouse: ['cdata-search', 'analytics', 'help-center'],
  };

  const allowedIds = ROLE_NAV[userRole] || ROLE_NAV.sales;
  const navItems = allItems.filter(i => i.id !== 'settings' && i.id !== 'admin-settings' && allowedIds.includes(i.id));
  const showSettings = allowedIds.includes('settings');
  const showAdminSettings = allowedIds.includes('admin-settings');

  return (
    <aside className="w-64 bg-slate-950 flex flex-col shrink-0 border-r border-slate-800 z-20 shadow-xl hidden md:flex">
      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b border-slate-800 shrink-0">
        <div className="bg-white rounded-lg px-3 py-2 w-full flex items-center justify-center shadow-sm">
          <img src="/logo.png" alt="Armellini Logistics" className="h-9 object-contain" />
        </div>
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-5 px-3 space-y-1">
        <div className="text-slate-500 font-mono text-[10px] font-bold tracking-wider uppercase mb-2 px-2 mt-1">Workspace</div>
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold transition-colors text-left cursor-pointer ${
                isActive
                  ? 'bg-blue-600/20 text-blue-400'
                  : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800/50'
              }`}
            >
              <Icon className="w-4 h-4" />
              {item.label}
            </button>
          );
        })}

        {/* Non-Floral module with submenu */}
        <div>
          <button
            onClick={() => {
              setNonFloralOpen(!nonFloralOpen);
              if (!nonFloralOpen) onNonFloralNav(nonFloralTab);
            }}
            className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-xs font-bold transition-colors cursor-pointer ${
              activeTab === 'non-floral'
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-slate-300 hover:bg-slate-900 hover:text-white'
            }`}
          >
            <span className="flex items-center gap-2.5"><Layers className="w-4 h-4" /> Non Floral LTL</span>
            {nonFloralOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
          {nonFloralOpen && (
            <div className="mt-1 ml-4 pl-3 border-l border-slate-800 space-y-0.5">
              {NON_FLORAL_SUBMENU.map((sub) => {
                const SubIcon = sub.icon;
                const isSubActive = activeTab === 'non-floral' && nonFloralTab === sub.id;
                return (
                  <button
                    key={sub.id}
                    onClick={() => onNonFloralNav(sub.id)}
                    className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-[11px] font-bold text-left transition-colors cursor-pointer ${
                      isSubActive
                        ? 'bg-blue-600/20 text-blue-400'
                        : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'
                    }`}
                  >
                    <SubIcon className="w-3.5 h-3.5" /> {sub.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* System Maintenance */}
        {showSettings && (
          <button
            onClick={() => setActiveTab('settings')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold transition-colors text-left cursor-pointer ${
              activeTab === 'settings'
                ? 'bg-blue-600/20 text-blue-400'
                : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800/50'
            }`}
          >
            <Settings className="w-4 h-4" />
            System Maintenance
          </button>
        )}

        {/* Admin Settings */}
        {showAdminSettings && (
          <button
            onClick={() => setActiveTab('admin-settings')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold transition-colors text-left cursor-pointer ${
              activeTab === 'admin-settings'
                ? 'bg-blue-600/20 text-blue-400'
                : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800/50'
            }`}
          >
            <ShieldAlert className="w-4 h-4 text-red-500" />
            Admin Settings
          </button>
        )}

      </nav>
      
      {/* Environment & Version badge */}
      <div className="p-3 border-t border-slate-800 text-center flex flex-col items-center gap-1.5 shrink-0">
        <span className="inline-block text-[10px] font-mono font-semibold tracking-widest text-slate-500 uppercase bg-slate-900 border border-slate-800 rounded px-2 py-0.5">
          ARMELLINI SECURE CRM
        </span>
        <div className="text-[10px] font-mono text-slate-400 flex items-center justify-center gap-1.5">
          <span className="font-bold text-blue-400">{APP_VERSION}</span>
          <span className="text-slate-600">|</span>
          <span className="text-slate-400">{APP_BUILD_DATETIME}</span>
        </div>
      </div>
    </aside>
  );
}
